/**
 * Path B (recomendado) do recurso m4a-recording: PCM mono → AAC-LC (mp4a.40.2)
 * via WebCodecs `AudioEncoder` → container MP4 via `mp4-muxer` → Blob `audio/mp4`.
 * Funciona em Chromium e Safari recente. Detecção em `audioPath.ts`.
 * Tipos WebCodecs via @types/dom-webcodecs.
 */
// O projectService do eslint não resolve os tipos de `mp4-muxer` (tipa `Muxer`
// como `error`), gerando no-unsafe-* falsos — vue-tsc resolve e o build passa.
// Desabilitado só aqui, no arquivo de interop com o muxer/WebCodecs.
/* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return */
import { Muxer, ArrayBufferTarget } from 'mp4-muxer';

export interface EncodeOptions {
    bitrate?: number;
    signal?: AbortSignal;
    onProgress?: (fraction: number) => void;
}

/**
 * Construtores WebCodecs injetáveis (legado). Na thread principal usamos os
 * globais nativos; o gancho de injeção fica para reuso fora do main thread.
 */
export interface M4aEncoderCtors {
    AudioEncoder: typeof globalThis.AudioEncoder;
    AudioData: typeof globalThis.AudioData;
}

const BLOCK = 1024;              // frames por AudioData
const YIELD_EVERY = BLOCK * 256; // cede ao event-loop (responsividade + checagem de abort)

export async function encodePcmToM4a(
    samples: Float32Array,
    sampleRate: number,
    opts: EncodeOptions = {},
    ctors?: Partial<M4aEncoderCtors>,
): Promise<Blob> {
    const { bitrate = 64000, signal, onProgress } = opts;

    const AudioEncoderCtor = ctors?.AudioEncoder ?? (typeof AudioEncoder !== 'undefined' ? AudioEncoder : undefined);
    const AudioDataCtor = ctors?.AudioData ?? (typeof AudioData !== 'undefined' ? AudioData : undefined);
    if (!AudioEncoderCtor || !AudioDataCtor) {
        throw new Error('WebCodecs AudioEncoder indisponível neste navegador.');
    }

    const muxer = new Muxer({
        target: new ArrayBufferTarget(),
        audio: { codec: 'aac', sampleRate, numberOfChannels: 1 },
        fastStart: 'in-memory',
    });

    let encodeError: unknown = null;
    const encoder = new AudioEncoderCtor({
        output: (chunk, meta) => muxer.addAudioChunk(chunk, meta),
        error: (e) => { encodeError = e; },
    });
    encoder.configure({ codec: 'mp4a.40.2', sampleRate, numberOfChannels: 1, bitrate });

    const total = samples.length;
    const throwIfAborted = () => {
        if (signal?.aborted) {
            try { encoder.close(); } catch { /* noop */ }
            throw new DOMException('Conversão de áudio cancelada.', 'AbortError');
        }
    };
    // O encoder pode falhar de forma assíncrona (ex.: codec sem suporte) via callback
    // de erro. Propaga o erro REAL em vez do "Unconfigured" secundário que viria depois.
    const throwIfErrored = () => {
        if (encodeError) {
            try { encoder.close(); } catch { /* noop */ }
            throw encodeError instanceof Error ? encodeError : new Error('Falha no encoder de áudio.');
        }
    };

    for (let i = 0; i < total; i += BLOCK) {
        throwIfAborted();
        throwIfErrored();
        const block = samples.subarray(i, Math.min(i + BLOCK, total));
        const audioData = new AudioDataCtor({
            format: 'f32',
            sampleRate,
            numberOfFrames: block.length,
            numberOfChannels: 1,
            timestamp: Math.round((i / sampleRate) * 1e6),
            data: block,
        });
        encoder.encode(audioData);
        audioData.close();

        onProgress?.(i / total);
        if (i % YIELD_EVERY === 0) await Promise.resolve();
    }

    await encoder.flush();
    encoder.close();
    throwIfAborted();
    if (encodeError) {
        throw encodeError instanceof Error ? encodeError : new Error('Falha ao codificar áudio (AAC/MP4).');
    }

    muxer.finalize();
    onProgress?.(1);
    return new Blob([muxer.target.buffer], { type: 'audio/mp4' });
}
